/**
 * buildExpiry.js — срок годности сборки.
 *
 * Зачем. Программа раздаётся коллегам как рабочая версия, и нужен рубеж, за
 * которым она сама скажет «эта сборка устарела, возьмите свежую». Иначе через
 * год кто-то будет ставить диагноз по копии, в которой не исправлено то, что
 * мы правили последние полгода.
 *
 * Правила, которые я заложил нарочно, и почему.
 *
 * 1. НИЧЕГО НЕ УДАЛЯЕТСЯ. Ни аннотаций, ни настроек, ни кэша. Просрочка — это
 *    только окно с текстом; данные врача не трогаем ни при каких условиях.
 *
 * 2. ПРЕДУПРЕЖДАЕМ ЗАРАНЕЕ, за две недели. Программа, которая перестала
 *    открываться без предупреждения посреди приёма — это не срок годности, а
 *    подстава.
 *
 * 3. РЕШЕНИЕ ОТДЕЛЕНО ОТ ПОСЛЕДСТВИЙ. Здесь только чистая функция без
 *    импортов: дали дату — получили вердикт. Её можно проверить тестом, не
 *    поднимая ни браузера, ни программы.
 *
 * 4. СРАВНИВАЕМ ПО UTC. Иначе у врача в другом часовом поясе сборка умрёт на
 *    сутки раньше или позже, и объяснить это будет нечем.
 *
 * 5. [12.0.40] ДАТА — В ОДНОМ ФАЙЛЕ srok.js В КОРНЕ ПРОЕКТА, А НЕ ЗДЕСЬ.
 *    Владелец: «сделать так, чтобы я не тебя просил, я мог сам продлять в
 *    дистрибутиве срок действия программы». Продлевает srok.cmd (спросит
 *    дату, проверит, запишет) или правка одной строки. Дата вшивается при
 *    сборке — после правки программу собирают заново.
 */

import СРОК_СТРОКОЙ from '../../srok.js';

/**
 * Дата срока из строки: 'ГГГГ-ММ-ДД' или 'ДД.ММ.ГГГГ'. Только настоящая
 * дата: 31.02 и 2027-13-01 — null (Date.UTC молча перекатил бы их в март и
 * следующий год). [12.0.40]
 * @returns {number|null} полночь UTC этого дня, мс
 */
export function разобратьДатуСрока(строка) {
  const s = String(строка ?? '').trim();
  let г, м, д;
  let x = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (x) [г, м, д] = [x[1], x[2], x[3]].map(Number);
  else if ((x = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/))) [д, м, г] = [x[1], x[2], x[3]].map(Number);
  else return null;
  if (м < 1 || м > 12 || д < 1 || д > 31) return null;
  const t = Date.UTC(г, м - 1, д, 0, 0, 0);
  const пров = new Date(t);
  return (пров.getUTCFullYear() === г && пров.getUTCMonth() === м - 1 && пров.getUTCDate() === д) ? t : null;
}

const _изФайла = разобратьДатуСрока(СРОК_СТРОКОЙ);

/**
 * Дата в srok.js записана верно. Если нет — программа НЕ запирается (иначе
 * опечатка остановила бы всех коллег разом), а пишет ошибку в журнал;
 * test.cmd на такой дате падает, srok.cmd неверную не запишет.
 */
export const СРОК_ЗАПИСАН_ВЕРНО = _изФайла !== null;

/** Граница работы сборки — полночь UTC дня из srok.js. */
export const EXPIRY_UTC = _изФайла ?? Date.UTC(2100, 0, 1, 0, 0, 0);

/** За сколько дней до конца начинать предупреждать. */
export const WARN_DAYS = 14;

const DAY = 24 * 60 * 60 * 1000;

/**
 * @param {number} [nowMs] текущее время; параметром — чтобы проверять тестом
 * @param {number} [expiryMs]
 * @returns {{expired: boolean, warn: boolean, daysLeft: number, dateText: string}}
 */
export function checkExpiry(nowMs = Date.now(), expiryMs = EXPIRY_UTC) {
  const left = expiryMs - nowMs;
  // Округляем ВВЕРХ: пока не наступила граница, «осталось 0 дней» писать
  // рано — это читается как «уже всё».
  const daysLeft = Math.ceil(left / DAY);
  const d = new Date(expiryMs);
  const dateText = `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.${d.getUTCFullYear()}`;
  return {
    expired: left <= 0,
    warn: left > 0 && daysLeft <= WARN_DAYS,
    daysLeft: Math.max(0, daysLeft),
    dateText,
  };
}

/** Текст для человека. Без запугивания и без технических подробностей. */
export function expiryMessage(state, version = '') {
  if (state.expired) {
    return {
      title: 'Срок этой сборки истёк',
      text: `Сборка ${version} рассчитана на работу до ${state.dateText}. `
        + 'Она больше не открывается, чтобы никто не работал по устаревшей версии. '
        + 'Ничего не удалено: аннотации, настройки и снимки на месте — они появятся в свежей сборке. '
        + 'Возьмите новую версию у автора.',
    };
  }
  return {
    title: 'Скоро понадобится свежая сборка',
    text: `Эта сборка работает до ${state.dateText} — осталось ${state.daysLeft} дн. `
      + 'Ничего не пропадёт: попросите у автора новую версию заранее, чтобы не остаться без программы в неудобный момент.',
  };
}

/**
 * Ключ продления. Хранится отпечатком SHA-256, а не открытым текстом.
 *
 * Чего это НЕ даёт, и вы должны это знать. Программа целиком выполняется на
 * машине пользователя, поэтому проверка выполняется там же. Отпечаток мешает
 * прочитать ключ, просто открыв файл сборки, но человек с навыком обойдёт
 * саму проверку — так устроена любая защита без сервера, без исключений.
 * Это рубеж от «работаю по старой сборке по забывчивости», а не от взлома.
 * Настоящая проверка требует обращения к вашему серверу.
 */
export const UNLOCK_HASH = '3b24c18958d7bd592c7b247f575d436a5de3f816d867982004ccd20efa42bdcc';

/**
 * Сверить введённый ключ с отпечатком.
 * @param {string} input
 * @returns {Promise<boolean>}
 */
export async function verifyUnlock(input, expected = UNLOCK_HASH) {
  try {
    const bytes = new TextEncoder().encode(String(input ?? ''));
    const buf = await crypto.subtle.digest('SHA-256', bytes);
    const hex = [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
    return hex === expected;
  } catch (e) { return false; }
}

/**
 * Метка о продлении привязана к КОНКРЕТНОМУ рубежу.
 *
 * Иначе один раз введённый ключ снял бы и все будущие сроки: перенесли рубеж
 * на год вперёд, а у человека в памяти браузера лежит старое «разрешено» — и
 * новая сборка молча не запирается.
 */
export function unlockKeyFor(expiryMs = EXPIRY_UTC) {
  return `mikhmel.unlocked.${expiryMs}`;
}

/* ══════════════════════════════════════════════════════════════════════════
 * ПЕРЕНОС РУБЕЖА БЕЗ КЛЮЧА — служебная возможность автора.
 *
 * Зачем. Ключ продления нужен коллеге, у которого сборка просрочилась. Автору
 * он не нужен и мешает: свою же программу приходится разблокировать паролем.
 * Поэтому есть второй путь — перенести саму дату, и он спрятан (как включается,
 * написано в ЭТАЛОН.md, а не в интерфейсе).
 *
 * ЧЕСТНО О ТОМ, ЧТО ЭТО ЗА ЗАЩИТА. Это защита незнанием, и ничем больше. Тот,
 * кто откроет память браузера или файл сборки, найдёт и ключ хранения, и способ.
 * Как и сказано выше про сам рубеж: без обращения к серверу иначе не бывает.
 * Рубеж существует, чтобы никто не работал по старой сборке ПО ЗАБЫВЧИВОСТИ.
 *
 * Два правила, заложенных нарочно:
 *
 *   1. ПЕРЕНОС ТОЛЬКО ВПЕРЁД. Меньше встроенной даты выставить нельзя — иначе
 *      случайная правка запирала бы работающую сборку.
 *   2. ЕСТЬ ПОТОЛОК. Не дальше пяти лет от встроенного рубежа: опечатка в годе
 *      («2206» вместо «2026») не должна превращать срок годности в вечность.
 * ══════════════════════════════════════════════════════════════════════════ */

/** Ключ хранения. Имя нарочно невнятное — по нему не догадаешься, что это срок. */
export const КЛЮЧ_ПЕРЕНОСА = 'mikhmel.svc.tz';

/** Потолок переноса — пять лет от встроенного рубежа. */
export const ПРЕДЕЛ_ПЕРЕНОСА = EXPIRY_UTC + 5 * 365 * DAY;

/**
 * Разобрать и проверить перенесённую дату.
 * @param {string|number} значение метка времени в мс или строка 'ГГГГ-ММ-ДД'
 * @returns {number|null} мс, если дата годная; иначе null
 */
export function разобратьПеренос(значение) {
  if (значение === null || значение === undefined || значение === '') return null;
  let ms = null;
  if (typeof значение === 'number') ms = значение;
  else {
    const s = String(значение).trim();
    if (/^\d+$/.test(s)) ms = Number(s);
    else {
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) {
        const [г, мес, д] = [Number(m[1]), Number(m[2]), Number(m[3])];
        // Проверять поля обязательно. Date.UTC молча ПЕРЕКАТЫВАЕТ лишнее:
        // «2026-13-45» превратилось бы в середину февраля 2027 — то есть
        // опечатка дала бы годную дату. Ловится тестом expiry_test.
        if (мес >= 1 && мес <= 12 && д >= 1 && д <= 31) {
          const t = Date.UTC(г, мес - 1, д, 0, 0, 0);
          const пров = new Date(t);
          if (пров.getUTCFullYear() === г && пров.getUTCMonth() === мес - 1 && пров.getUTCDate() === д) ms = t;
        }
      }
    }
  }
  if (!Number.isFinite(ms)) return null;
  if (ms <= EXPIRY_UTC) return null;          // только вперёд
  if (ms > ПРЕДЕЛ_ПЕРЕНОСА) return null;      // и не дальше потолка
  return ms;
}

/**
 * Прочитать перенос из хранилища.
 * @param {(k: string) => (string|null)} читать
 */
export function перенесённыйСрок(читать) {
  try { return разобратьПеренос(читать(КЛЮЧ_ПЕРЕНОСА)); } catch (e) { return null; }
}

/**
 * Действующий рубеж: встроенный или перенесённый — что позже.
 * @param {(k: string) => (string|null)} [читать]
 */
export function действующийСрок(читать) {
  const п = типЧитателя(читать) ? перенесённыйСрок(читать) : null;
  return (п && п > EXPIRY_UTC) ? п : EXPIRY_UTC;
}

function типЧитателя(f) { return typeof f === 'function'; }

/**
 * Записать перенос. Возвращает применённую метку или null, если дата не годная.
 * @param {(k: string, v: string) => void} записать
 * @param {string|number} значение
 */
export function перенестиСрок(записать, значение) {
  const ms = разобратьПеренос(значение);
  if (!ms) return null;
  try { записать(КЛЮЧ_ПЕРЕНОСА, String(ms)); } catch (e) { return null; }
  return ms;
}

/** Снять перенос — вернуться к встроенной дате. */
export function забытьПеренос(удалить) {
  try { удалить(КЛЮЧ_ПЕРЕНОСА); } catch (e) { /* приватный режим */ }
}

export default {
  СРОК_ЗАПИСАН_ВЕРНО, разобратьДатуСрока,
  EXPIRY_UTC, WARN_DAYS, checkExpiry, expiryMessage, UNLOCK_HASH, verifyUnlock, unlockKeyFor,
  КЛЮЧ_ПЕРЕНОСА, ПРЕДЕЛ_ПЕРЕНОСА, разобратьПеренос, перенесённыйСрок, действующийСрок,
  перенестиСрок, забытьПеренос,
};
